import { useCallback, type ReactNode } from 'react'
import { isNotDefined, type EntityId, type Preferences } from '@terros/common'
import { notifications } from '@mantine/notifications'
import { Select, type SelectProps } from '@mantine/core'
import { useToastError } from '@hooks'
import { useTranslation, useUpdatePreference, type SelectItem } from '@front/model'
import { PreferenceInput } from './PreferenceInput'

type SelectKeys = keyof Pick<
  Preferences,
  | 'closerStatus'
  | 'closerModel'
  | 'allowDuplicateAccounts'
  | 'areaVisibility'
  | 'country'
  | 'bookingPercent'
  | 'closerInteraction'
  | 'dispositionMode'
  | 'recordingReminder'
  | 'strictSubareaAssignment'
  | 'unitSystem'
>

type Props<K extends SelectKeys> = Omit<SelectProps, 'data' | 'value' | 'onChange' | 'label' | 'description'> & {
  data: SelectItem[]
  preference: K
  targetId: EntityId
  description?: ReactNode
}

export const PreferenceSelect = <K extends SelectKeys>({
  preference,
  targetId,
  disabled,
  description: customDescription,
  ...rest
}: Props<K>): ReactNode => {
  const { t } = useTranslation()
  const { update, error: updateError } = useUpdatePreference(preference, targetId)
  useToastError(updateError)

  const setValue = useCallback(
    async (value: string | null) => {
      if (value === null) throw new Error('unexpected select cleared')
      if (preference === 'bookingPercent') {
        await update(Number(value) as Preferences[K])
      } else {
        await update(value as Preferences[K])
      }
      notifications.show({ message: t('Preference saved'), color: 'green' })
    },
    [update, preference, t]
  )

  return (
    <PreferenceInput targetId={targetId} preference={preference}>
      {({ data, disabled: isReadOnly }) => (
        <Select
          {...rest}
          disabled={disabled || isReadOnly}
          flex={1}
          value={transformValue(data.current ?? data.inherited)}
          onChange={setValue}
        />
      )}
    </PreferenceInput>
  )
}

const transformValue = (value?: string | number | null): string | undefined | null => {
  if (isNotDefined(value)) return
  if (typeof value === 'number') return value.toString()
  return value
}
